import React, { useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Theme from 'theme';
import { useMutation } from 'react-query';
import { ToastProps, ToastType } from 'components/Common/Toast';
import { changeFreeTrialPassword } from 'pages/Dashboard/services/freeTrial.service';
import http from 'utils/http';
import { trackMixpanelEvent, logSentryError } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import { useFreeTrialOnboarding } from 'pages/FreeTrialActivation/components/FreeTrialOnboardingContext';

interface CreatePasswordProps {
  onNext?: () => void;
  step?: number;
  totalSteps?: number;
  token?: string;
  setToastProps: (props: ToastProps | null) => void;
}

export default function CreatePassword({
  onNext,
  step,
  totalSteps = 6,
  token,
  setToastProps,
}: CreatePasswordProps) {
  useEffect(() => {
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_PASSWORD_DIALOG_VIEWED);
  }, []);

  const { createPassword, setCreatePassword } = useFreeTrialOnboarding();
  const { password, error } = createPassword;

  const mutation = useMutation({
    mutationFn: (pwd: string) => changeFreeTrialPassword(http.post, pwd, token),
    onError: (errorMessage) => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_PASSWORD_ERROR);
      logSentryError(`Error setting password - ${(errorMessage as Error)?.message}`);
      setToastProps({
        message: 'Failed to set password. Please try again.',
        open: true,
        type: ToastType.error,
      });
    },
    onSuccess: () => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_PASSWORD_SUCCESS);
      if (onNext) onNext();
    },
  });


  const requirements = [
    {
      label: 'Include a lowercase letter (a–z)',
      test: (pwd: string) => /[a-z]/.test(pwd),
    },
    {
      label: 'Include an uppercase letter (A–Z)',
      test: (pwd: string) => /[A-Z]/.test(pwd),
    },
    {
      label: 'Include a number',
      test: (pwd: string) => /[0-9]/.test(pwd),
    },
    {
      label: 'Include a special character (#, @, !, $, etc.)',
      test: (pwd: string) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
    },
    {
      label: 'Be at least 8 characters',
      test: (pwd: string) => pwd.length >= 8,
    },
    {
      label: 'Contain no spaces',
      test: (pwd: string) => !/\s/.test(pwd),
    },
  ];

  const passwordStrengthValidity = password
    ? requirements.map((req) => req.test(password))
    : requirements.map(() => false);
  const isValid = passwordStrengthValidity.every(Boolean);

  const handleCreate = () => {
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_PASSWORD_BUTTON_CLICKED);
    if (!isValid) {
      setCreatePassword((prev) => ({ ...prev, error: 'Password does not meet all requirements.' }));
      return;
    }
    setCreatePassword((prev) => ({ ...prev, error: '' }));
    mutation.mutate(password);
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: 400, fontSize: 34, pb: 0, pl: 3, pt: 2 }}>
        Create your password
      </DialogTitle>
      <LinearProgress
        variant='determinate'
        value={(step as number / totalSteps as number) * 100}
        color='success'
        sx={{
          width: '100%',
          borderRadius: '24px 24px 0 0',
          height: 4,
          my: 1,
        }}
      />
      <DialogContent sx={{ pt: 1 }}>
        <Typography fontSize={24} mb={3}>
          Please set your password
        </Typography>
        <TextField
          label='Password'
          type='password'
          variant='outlined'
          fullWidth
          sx={{ mb: 3, width: '65%' }}
          value={password}
          onChange={(e) => setCreatePassword((prev) => ({ ...prev, password: e.target.value }))}
          error={!!error}
          helperText={error}
          className='fs-mask'
        />
        <Typography fontSize={16}>
          Your password must:
        </Typography>
        <List dense sx={{ mb: 2, pl: 2 }}>
          {requirements.map((req, idx) => (
            <ListItem key={req.label} disableGutters>
              <ListItemIcon sx={{ minWidth: 26 }}>
                {passwordStrengthValidity[idx] ? (
                  <DoneOutlinedIcon sx={{ color: Theme.custom.colors.lightSuccessMain }} />
                ) : (
                  <FiberManualRecordIcon
                    sx={{ color: Theme.custom.colors.lightTextPrimary, fontSize: 10 }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={req.label}
                primaryTypographyProps={{
                  sx: {
                    color: passwordStrengthValidity[idx]
                      ? Theme.custom.colors.lightSuccessMain
                      : Theme.custom.colors.lightTextPrimary,
                    fontSize: '16px',
                    fontWeight: 400,
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
        <Button
          variant='contained'
          sx={{
            background: Theme.custom.colors.lightPrimaryMain,
            color: Theme.custom.colors.white,
            fontSize: 15,
            borderRadius: 8,
            boxShadow: 'none',
            py: 1.5,
            px: 6,
          }}
          disabled={!password || mutation.isLoading}
          onClick={handleCreate}
        >
          CREATE PASSWORD
        </Button>
      </DialogContent>
    </>
  );
}
